import React, { useMemo, useState } from 'react';
import { PortalContainerProvider } from '@highereducation/hen-ui';
import type { SlotPlacementId } from '../../types/types';
import { useSiteConfig } from '../../providers/ExperienceConfigProvider';
import { resolvePlacementMinHeight } from '../../utils/resolvePlacementMinHeight';

export interface ExperiencePlacementProps {
  placementId: SlotPlacementId;
  children: React.ReactNode;
  needsPlaceholder?: boolean;
  /**
   * When true, the flow content has rendered and the placeholder min-height
   * reservation is released — the container keeps its layout classes but
   * sizes to its content. Without this, widgets shorter than the reserved
   * min-height keep permanent whitespace below them.
   */
  contentLoaded?: boolean;
  /**
   * When true, renders a `[data-de-portal]` container and provides it to
   * Radix UI portals via context so they receive styles from the scoped
   * stylesheet. Should match the `scoped` prop on `DynamicExperience`.
   */
  scoped?: boolean;
  /**
   * Explicit container for Radix portals. When provided (with `scoped`),
   * overlays portal here instead of the in-flow `[data-de-portal]` container.
   * The affiliate-bundle host passes a body-level overlay shadow root so
   * overlays escape host list-item stacking contexts. Omitted on
   * hen-site, where the in-flow container behavior is unchanged.
   */
  portalContainer?: HTMLElement | null;
}

/**
 * Wrapper component that handles placement configuration
 * and provides minimum height based on configuration
 */
export const ExperiencePlacement: React.FC<ExperiencePlacementProps> = ({
  placementId,
  children,
  needsPlaceholder = true,
  contentLoaded = false,
  scoped = false,
  portalContainer,
}) => {
  const siteConfig = useSiteConfig();

  const { desktop: defaultMinHeightDesktop, mobile: defaultMinHeightMobile } =
    useMemo(
      () => resolvePlacementMinHeight(placementId, siteConfig?.configs),
      [placementId, siteConfig?.configs],
    );

  // This state hook is declared unconditionally, but it is only used when
  // `scoped` is true to provide the in-flow `[data-de-portal]` container to
  // portals. An explicit `portalContainer` (affiliate host) takes precedence.
  const [inFlowPortalContainer, setInFlowPortalContainer] =
    useState<HTMLElement | null>(null);

  const content = scoped ? (
    <>
      {/* Mount point for Radix portals (Select, Dialog, Tooltip, Drawer) —
          keeps portaled content inside the [data-de-portal] CSS scope. When an
          explicit `portalContainer` is supplied, that body-level overlay root
          wins and this in-flow container stays unused. */}
      <div ref={setInFlowPortalContainer} data-de-portal />
      <PortalContainerProvider value={portalContainer ?? inFlowPortalContainer}>
        {children}
      </PortalContainerProvider>
    </>
  ) : (
    children
  );

  return (
    <div
      data-de
      data-placement-id={placementId}
      className={
        needsPlaceholder
          ? // The min-height only reserves space while the flow loads (CLS
            // guard); once content renders the container sizes to it.
            contentLoaded
            ? 'col-span-full w-full'
            : 'col-span-full w-full min-h-[--placement-min-mobile] md:min-h-[--placement-min-desktop]'
          : ''
      }
      style={
        {
          '--placement-min-mobile': `${defaultMinHeightMobile}px`,
          '--placement-min-desktop': `${defaultMinHeightDesktop}px`,
        } as React.CSSProperties
      }
    >
      {content}
    </div>
  );
};

export default ExperiencePlacement;
